import React, { useEffect, useState } from 'react';
import './TransportRoutes.css';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  App,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  InboxOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
  buildTransportRouteLabel,
  normalizeTransportAddressList,
} from '../modules/orders/transportAddressUtils';
import {
  detectTransportStatusView,
  mapTransportMutationErrorToMessage,
  renderTransportTypeTag,
  TRANSPORT_STATUS_VIEW_GROUPS,
} from '../modules/transports/transportRouteViews';
import type { TransportStatusView } from '../modules/transports/transportRouteViews';
import {
  canDeleteTransportRoute,
  canManageTransportRoutes,
} from '../modules/transports/transportRouteAccessPolicy';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatMoneyPl } from '../utils/currency';
import { formatDate } from '../utils/dateUtils';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatUnit } from '../utils/units';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';

const { RangePicker } = DatePicker;
interface TransportProductSummary {
  id: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  width: number | null;
  height: number | null;
  length: number | null;
  thickness: number | null;
  productCategoryDimensionDisplayProfile?: string | null;
}

interface TransportRouteOrderSummary {
  order?: {
    customer?: {
      name?: string | null;
    } | null;
  } | null;
}

interface TransportProductEntry {
  key: string;
  name: string;
  quantityLabel: string | null;
  dimensionsLabel: string | null;
}

interface TransportListItem {
  id: string;
  orderId: string;
  orderNumber: string;
  displayOrderNumber: string;
  clientOrderNumber: string | null;
  orderDescription: string | null;
  orderDate: string | null;
  customerName: string;
  transportCompany: string;
  transportContact: string | null;
  status: string;
  loadingDate: string | null;
  estimatedDelivery: string | null;
  actualDelivery: string | null;
  loadingAddresses: string[];
  deliveryAddresses: string[];
  packages: number | null;
  totalWeight: number | null;
  totalVolume: number | null;
  transportRate: number | null;
  currency: string;
  customer: string | null;
  dimensions: string | null;
  notes: string | null;
  driverName: string | null;
  driverPhone: string | null;
  licensePlate1: string | null;
  licensePlate2: string | null;
  orderFactoryLocation: string | null;
  orderDeliveryLocation: string | null;
  products: TransportProductSummary[];
  routeOrders: TransportRouteOrderSummary[];
}

const EMPTY_RANGE: [Dayjs | null, Dayjs | null] = [null, null];

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeNullableText(value: unknown): string | null {
  const normalizedValue = normalizeText(value);
  return normalizedValue || null;
}

function normalizeNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizeProducts(raw: unknown): TransportProductSummary[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;

      return {
        id: normalizeText(record.id),
        name: normalizeText(record.name),
        quantity: normalizeNumber(record.quantity),
        unit: normalizeNullableText(record.unit),
        width: normalizeNumber(record.width),
        height: normalizeNumber(record.height),
        length: normalizeNumber(record.length),
        thickness: normalizeNumber(record.thickness),
      };
    })
    .filter((product) => product.name);
}

function normalizeTransportList(raw: unknown): TransportListItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      // The backend list endpoint always emits routeOrders (transportRepository
      // buildTransportListRow); there is no legacy `orders` field to fall back to.
      const routeOrdersSource = Array.isArray(record.routeOrders) ? record.routeOrders : [];

      return {
        id: normalizeText(record.id),
        orderId: normalizeText(record.orderId),
        orderNumber: normalizeText(record.orderNumber),
        displayOrderNumber: normalizeText(record.displayOrderNumber) || normalizeText(record.orderNumber),
        clientOrderNumber: normalizeNullableText(record.clientOrderNumber),
        orderDescription: normalizeNullableText(record.orderDescription),
        orderDate: normalizeNullableText(record.orderDate),
        customerName: normalizeText(record.customerName),
        transportCompany: normalizeText(record.transportCompany),
        transportContact: normalizeNullableText(record.transportContact),
        status: normalizeText(record.status),
        loadingDate: normalizeNullableText(record.loadingDate),
        estimatedDelivery: normalizeNullableText(record.estimatedDelivery),
        actualDelivery: normalizeNullableText(record.actualDelivery),
        loadingAddresses: normalizeTransportAddressList(
          record.loadingAddresses ?? record.loadingAddress
        ),
        deliveryAddresses: normalizeTransportAddressList(
          record.deliveryAddresses ?? record.deliveryAddress
        ),
        packages: normalizeNumber(record.packages),
        totalWeight: normalizeNumber(record.totalWeight),
        totalVolume: normalizeNumber(record.totalVolume),
        transportRate: normalizeNumber(record.transportRate),
        currency: normalizeText(record.currency) || 'USD',
        customer: normalizeNullableText(record.customer) || normalizeNullableText(record.customerName),
        dimensions: normalizeNullableText(record.dimensions),
        notes: normalizeNullableText(record.notes),
        driverName: normalizeNullableText(record.driverName),
        driverPhone: normalizeNullableText(record.driverPhone),
        licensePlate1: normalizeNullableText(record.licensePlate1),
        licensePlate2: normalizeNullableText(record.licensePlate2),
        orderFactoryLocation: normalizeNullableText(record.orderFactoryLocation),
        orderDeliveryLocation: normalizeNullableText(record.orderDeliveryLocation),
        products: normalizeProducts(record.products),
        routeOrders: routeOrdersSource as TransportRouteOrderSummary[],
      };
    })
    .filter((transport) =>
      Boolean(transport.id)
      && (Array.isArray(transport.routeOrders) ? transport.routeOrders.length > 0 : false),
    );
}

function buildLicensePlates(plate1: string | null, plate2: string | null): string | null {
  const joined = [plate1, plate2].filter((value): value is string => Boolean(value)).join(' / ');
  return joined || null;
}

function buildProductEntries(products: TransportProductSummary[]): TransportProductEntry[] {
  return products
    .map((product, index) => {
      const name = normalizeText(product.name);

      if (!name) {
        return null;
      }

      return {
        key: product.id || `${name}-${index}`,
        name,
        quantityLabel:
          product.quantity === null ? null : `${product.quantity} ${formatUnit(product.unit)}`,
        dimensionsLabel: formatProductDimensions(product) || null,
      };
    })
    .filter((entry): entry is TransportProductEntry => Boolean(entry));
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'default',
    SCHEDULED: 'processing',
    LOADING: 'gold',
    IN_TRANSIT: 'blue',
    DELIVERED: 'success',
    CANCELLED: 'default',
    PLANNED: 'processing',
    DELAYED: 'error',
  };

  return colors[status] || 'default';
}

function getStatusIcon(status: string): React.ReactNode {
  const icons: Record<string, React.ReactNode> = {
    PENDING: <ClockCircleOutlined />,
    SCHEDULED: <CalendarOutlined />,
    LOADING: <InboxOutlined />,
    IN_TRANSIT: <CarOutlined />,
    DELIVERED: <CheckCircleOutlined />,
    CANCELLED: <StopOutlined />,
    PLANNED: <CalendarOutlined />,
    DELAYED: <WarningOutlined />,
  };

  return icons[status] || <ClockCircleOutlined />;
}

function getTransportOrderCount(record: TransportListItem): number {
  return Array.isArray(record.routeOrders) ? record.routeOrders.length : 0;
}

// S36 (coordinator triage, 2026-07-07, external run #3): this column used to
// render via Intl.NumberFormat's 'currency' style, which prints a currency
// SYMBOL ("4 500,00 zł" for PLN) — inconsistent with the transport detail
// page, which shows the ISO code ("4 500,00 PLN"). formatMoneyPl in
// utils/currency.ts is the single home for that ISO-code convention; this
// wrapper keeps only the page-local null -> '—' placeholder (em dash,
// unified with the detail page and the rest of the app's empty cells).
function formatMoney(amount: number | null, currency: string): string {
  if (amount === null) {
    return '—';
  }

  return formatMoneyPl(amount, currency || 'USD');
}

function formatLoadingDate(value: string | null): string {
  if (!value) {
    return '-';
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD/MM/YY') : '-';
}

function renderExpandedList(items: string[]): React.ReactNode {
  if (items.length === 0) {
    return '-';
  }

  return (
    <Space direction="vertical" size={4}>
      {items.map((item, index) => (
        <div key={`${item}-${index}`} style={{ whiteSpace: 'pre-line' }}>
          {item}
        </div>
      ))}
    </Space>
  );
}

function parseStatusFilter(value: string | null): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function parseDateParam(value: string | null): Dayjs | null {
  if (!value) {
    return null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue : null;
}

const TransportRoutes: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transports, setTransports] = useState<TransportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [transportCompanyFilter, setTransportCompanyFilter] = useState(
    () => searchParams.get('transportCompany') || ''
  );
  const [productsFilter, setProductsFilter] = useState(() => searchParams.get('products') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(
    () => parseStatusFilter(searchParams.get('status'))
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orderDateRange, setOrderDateRange] = useState<[Dayjs | null, Dayjs | null]>(() => [
    parseDateParam(searchParams.get('orderDateFrom')),
    parseDateParam(searchParams.get('orderDateTo')),
  ]);
  const [pendingDelete, setPendingDelete] = useState<TransportListItem | null>(null);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canDeleteTransports = canDeleteTransportRoute(currentUser);
  const canViewTransportCosts = authService.canViewTransportCosts();
  const activeFilterCount = [
    Boolean(searchTerm.trim()),
    Boolean(transportCompanyFilter.trim()),
    Boolean(productsFilter.trim()),
    statusFilter.length > 0,
    Boolean(orderDateRange[0] && orderDateRange[1]),
  ].filter(Boolean).length;
  const currentStatusView = detectTransportStatusView(statusFilter);

  useEffect(() => {
    const nextSearchParams = new URLSearchParams();

    if (searchTerm.trim()) {
      nextSearchParams.set('search', searchTerm.trim());
    }

    if (transportCompanyFilter.trim()) {
      nextSearchParams.set('transportCompany', transportCompanyFilter.trim());
    }

    if (productsFilter.trim()) {
      nextSearchParams.set('products', productsFilter.trim());
    }

    if (statusFilter.length > 0) {
      nextSearchParams.set('status', statusFilter.join(','));
    }

    if (orderDateRange[0] && orderDateRange[1]) {
      nextSearchParams.set('orderDateFrom', orderDateRange[0].startOf('day').toISOString());
      nextSearchParams.set('orderDateTo', orderDateRange[1].endOf('day').toISOString());
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [orderDateRange, productsFilter, searchTerm, setSearchParams, statusFilter, transportCompanyFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void fetchTransports();
    }, 250);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDateRange, productsFilter, searchTerm, statusFilter, transportCompanyFilter]);

  const fetchTransports = async (): Promise<void> => {
    setLoading(true);

    try {
      const params: Record<string, string> = {};

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (transportCompanyFilter.trim()) {
        params.transportCompany = transportCompanyFilter.trim();
      }

      if (productsFilter.trim()) {
        params.products = productsFilter.trim();
      }

      if (statusFilter.length > 0) {
        params.status = statusFilter.join(',');
      }

      if (orderDateRange[0] && orderDateRange[1]) {
        params.orderDateFrom = orderDateRange[0].startOf('day').toISOString();
        params.orderDateTo = orderDateRange[1].endOf('day').toISOString();
      }

      const response = await api.get('/transport-routes', { params });
      setTransports(normalizeTransportList(response.data));
    } catch (error) {
      message.error(t('transport:errorFetchingRoutes'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchTransports);

  const clearFilters = (): void => {
    setSearchTerm('');
    setTransportCompanyFilter('');
    setProductsFilter('');
    setStatusFilter([]);
    setOrderDateRange(EMPTY_RANGE);
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) {
      return;
    }

    try {
      await api.delete(`/transport-routes/${pendingDelete.id}`);
      message.success(t('transport:messages.deleteSuccess'));
      setPendingDelete(null);
      await fetchTransports();
    } catch (error: any) {
      // MED (i18n error mapping): the delete route's WZ_TRANSPORT_LINK_EXISTS
      // response shape is { code, error, wzDocuments } (handleTransportMutationError
      // in transportRoutes.js), NOT `.message` — map the code to Polish-first
      // copy instead of falling through to the generic error.
      const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
      message.error(mapped || error?.response?.data?.error || t('transport:messages.deleteError'));
    }
  };

  const getRowMenuItems = (record: TransportListItem): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: t('transport:menu.view'),
      },
    ];

    if (canManageTransports) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('transport:menu.edit'),
      });
    }

    if (canDeleteTransports) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('transport:menu.delete'),
        danger: true,
      });
    }

    return items;
  };

  const columns: ColumnsType<TransportListItem> = [
    {
      title: t('transport:table.orderNumber'),
      key: 'order',
      width: 140,
      sorter: (left, right) => left.displayOrderNumber.localeCompare(right.displayOrderNumber),
      render: (_, record) => {
        const tooltipParts = [record.displayOrderNumber, record.clientOrderNumber, record.orderDescription].filter(Boolean);

        return (
          <Tooltip title={tooltipParts.join(' — ') || ''}>
            <button
              type="button"
              className="transport-routes-table__cell transport-routes-table__order-link"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/app/transports/${record.id}`);
              }}
              aria-label={t('transport:menu.view')}
            >
              <span className="transport-routes-table__order-number">{record.displayOrderNumber || '-'}</span>
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: t('transport:table.route'),
      key: 'route',
      width: 260,
      render: (_, record) => {
        const orderCount = getTransportOrderCount(record);
        const route = buildTransportRouteLabel(
          record.loadingAddresses,
          record.deliveryAddresses,
          record.orderFactoryLocation,
          record.orderDeliveryLocation,
        );

        if (route.label === '-') {
          return <span className="transport-routes-table__cell-text">-</span>;
        }

        return (
          <Tooltip title={<pre className="transport-routes-table__route-tooltip">{route.tooltip}</pre>}>
            <span
              className="transport-routes-table__cell transport-routes-table__route-cell"
              aria-label={route.label}
            >
              <span className="transport-routes-table__route-endpoint" title={route.origin}>
                {route.origin}
              </span>
              <span className="transport-routes-table__route-arrow" aria-hidden="true">→</span>
              <span className="transport-routes-table__route-endpoint" title={route.destination}>
                {route.destination}
              </span>
              {renderTransportTypeTag(t, orderCount, { marginInlineEnd: 0 })}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t('transport:table.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 170,
      sorter: (left, right) => left.customerName.localeCompare(right.customerName),
      render: (value: string) =>
        value ? (
          <Tooltip title={value}>
            <span className="transport-routes-table__cell transport-routes-table__cell--wrap transport-routes-table__cell-text">{value}</span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: t('transport:table.products'),
      key: 'products',
      width: 320,
      className: 'transport-routes-table__products-column',
      render: (_, record) => {
        const entries = buildProductEntries(record.products);

        if (entries.length === 0) {
          return <span className="transport-routes-table__cell-text">-</span>;
        }

        return (
          <div className="transport-routes-table__products-cell">
            {entries.map((entry) => (
              <div key={entry.key} className="transport-routes-table__product-row">
                <span className="transport-routes-table__product-quantity">
                  {entry.quantityLabel ?? ''}
                </span>
                <div className="transport-routes-table__product-main">
                  <Tooltip title={entry.name}>
                    <span className="transport-routes-table__product-name">{entry.name}</span>
                  </Tooltip>
                  {entry.dimensionsLabel ? (
                    <span className="transport-routes-table__product-dimensions">
                      {entry.dimensionsLabel}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: t('transport:table.loadingDate'),
      dataIndex: 'loadingDate',
      key: 'loadingDate',
      width: 110,
      sorter: (left, right) =>
        dayjs(left.loadingDate).valueOf() - dayjs(right.loadingDate).valueOf(),
      render: (value: string | null) => (
        <span className="transport-routes-table__cell transport-routes-table__cell-text">
          {formatLoadingDate(value)}
        </span>
      ),
    },
    {
      title: t('transport:table.transportCompany'),
      dataIndex: 'transportCompany',
      key: 'transportCompany',
      width: 190,
      sorter: (left, right) => left.transportCompany.localeCompare(right.transportCompany),
      render: (value: string, record) => (
        <Tooltip title={record.transportContact ? `${value} — ${record.transportContact}` : value}>
          <span className="transport-routes-table__cell transport-routes-table__cell--wrap transport-routes-table__cell-text transport-routes-table__cell-text--strong">
            {value || '-'}
          </span>
        </Tooltip>
      ),
    },
    ...(canViewTransportCosts ? [{
      title: t('transport:table.rate'),
      key: 'transportRate',
      width: 120,
      align: 'right',
      sorter: (left: TransportListItem, right: TransportListItem) => (left.transportRate || 0) - (right.transportRate || 0),
      render: (_: unknown, record: TransportListItem) => (
        <span className="transport-routes-table__cell transport-routes-table__rate">
          {formatMoney(record.transportRate, record.currency)}
        </span>
      ),
    }] as ColumnsType<TransportListItem> : []),
    {
      title: t('transport:table.licensePlate'),
      key: 'licensePlates',
      width: 150,
      render: (_, record) => {
        const value = buildLicensePlates(record.licensePlate1, record.licensePlate2);

        if (!value) {
          return <span className="transport-routes-table__cell-text">-</span>;
        }

        return (
          <Tooltip title={value}>
            <span className="transport-routes-table__cell transport-routes-table__cell-text transport-routes-table__cell-text--mono">
              {value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t('transport:table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 108,
      align: 'center',
      className: 'transport-routes-table__status-column',
      sorter: (left, right) => left.status.localeCompare(right.status),
      render: (value: string) => {
        const label = t(`transport:status.${value.toLowerCase()}`, { defaultValue: value });

        return (
          <Tooltip title={label}>
            <Tag
              color={getStatusColor(value)}
              bordered={false}
              aria-label={label}
              style={{
                marginInlineEnd: 0,
                width: 28,
                minWidth: 28,
                height: 28,
                paddingInline: 0,
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
              }}
            >
              {getStatusIcon(value)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'menu',
      width: 56,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'view') {
                navigate(`/app/transports/${record.id}`);
              }

              if (key === 'edit') {
                navigate(`/app/transports/${record.id}/edit`);
              }

              if (key === 'delete') {
                setPendingDelete(record);
              }
            },
          }}
        >
          <Button
            className="transport-routes-row-menu-button"
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('transport:menu.open')}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className="transport-routes-page">
        <div className="transport-routes-page__workspace-bar">
          <div className="transport-routes-page__view-chips" role="tablist" aria-label={t('transport:transportRoutes')}>
            {(['all', 'pending', 'active', 'archived'] as TransportStatusView[]).map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={currentStatusView === view}
                className={`transport-routes-page__view-chip ${
                  currentStatusView === view ? 'transport-routes-page__view-chip--active' : ''
                }`}
                onClick={() => {
                  if (view === 'all') {
                    setStatusFilter([]);
                    return;
                  }

                  const group = TRANSPORT_STATUS_VIEW_GROUPS[view as Exclude<TransportStatusView, 'custom' | 'all'>];
                  if (group) setStatusFilter(group);
                }}
              >
                {t(`transport:tabs.${view}`)}
              </button>
            ))}
            {currentStatusView === 'custom' && (
              <button
                type="button"
                role="tab"
                aria-selected
                className="transport-routes-page__view-chip transport-routes-page__view-chip--active"
                onClick={() => setFiltersOpen(true)}
              >
                {t('transport:tabs.custom')}
              </button>
            )}
          </div>

          <div className="transport-routes-page__quick-actions">
            <Input
              className="transport-routes-page__quick-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('transport:searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <Button
              className={`transport-routes-page__filter-button ${
                filtersOpen ? 'transport-routes-page__filter-button--open' : ''
              }`}
              icon={<FilterOutlined />}
              onClick={() => setFiltersOpen((current) => !current)}
            >
              {t('transport:filters')}
              {activeFilterCount > 0 && (
                <span className="transport-routes-page__filter-count">{activeFilterCount}</span>
              )}
            </Button>
            <span className="transport-routes-page__count">
              {t('transport:totalRoutes', { total: transports.length })}
            </span>
            {canManageTransports && (
              <Button
                className="transport-routes-page__create-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/app/transports/new')}
              >
                {t('transport:createTransport')}
              </Button>
            )}
            <Button
              className="transport-routes-page__refresh-button"
              icon={<ReloadOutlined />}
              onClick={() => void fetchTransports()}
            >
              {t('transport:actions.refresh')}
            </Button>
          </div>
        </div>

        <div className="transport-routes-page__filter-summary">
          <button
            type="button"
            className="transport-routes-page__filter-summary-button"
            onClick={() => setFiltersOpen((current) => !current)}
          >
            <FilterOutlined />
            <span>
              {activeFilterCount > 0
                ? t('transport:filtersActive', { count: activeFilterCount })
                : t('transport:noActiveFilters')}
            </span>
          </button>
          {activeFilterCount > 0 && (
            <Button size="small" type="text" onClick={clearFilters}>
              {t('common:clearFilters')}
            </Button>
          )}
        </div>

        {filtersOpen && (
          <div className="transport-routes-filters">
            <Row gutter={[16, 16]}>
                    <Col xs={24} md={12} xl={8}>
                      <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder={t('transport:searchPlaceholder')}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </Col>
                    <Col xs={24} md={12} xl={5}>
                      <Input
                        allowClear
                        placeholder={t('transport:filtersTransportCompany')}
                        value={transportCompanyFilter}
                        onChange={(event) => setTransportCompanyFilter(event.target.value)}
                      />
                    </Col>
                    <Col xs={24} md={12} xl={5}>
                      <Input
                        allowClear
                        placeholder={t('transport:filtersProducts')}
                        value={productsFilter}
                        onChange={(event) => setProductsFilter(event.target.value)}
                      />
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={t('transport:selectStatus')}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                          { value: 'PENDING', label: t('transport:status.pending') },
                          { value: 'SCHEDULED', label: t('transport:status.scheduled') },
                          { value: 'LOADING', label: t('transport:status.loading') },
                          { value: 'IN_TRANSIT', label: t('transport:status.in_transit') },
                          { value: 'DELIVERED', label: t('transport:status.delivered') },
                          { value: 'CANCELLED', label: t('transport:status.cancelled') },
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <RangePicker
                        style={{ width: '100%' }}
                        value={orderDateRange}
                        onChange={(value) =>
                          setOrderDateRange(value ? [value[0], value[1]] : EMPTY_RANGE)
                        }
                        placeholder={[
                          t('transport:filtersOrderDateFrom'),
                          t('transport:filtersOrderDateTo'),
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={12} xl={4}>
                      <Button block onClick={clearFilters}>
                        {t('common:clearFilters')}
                      </Button>
                    </Col>
            </Row>
          </div>
        )}

          <Card className="transport-routes-table-shell" bodyStyle={{ padding: 0 }}>
            <Table<TransportListItem>
              className="transport-routes-table"
              rowClassName={(_, index) =>
                index % 2 === 0
                  ? 'transport-routes-table__row transport-routes-table__row--even'
                  : 'transport-routes-table__row'
              }
              rowKey="id"
              loading={loading}
              dataSource={transports}
              columns={columns}
              scroll={{ x: 1822 }}
              expandable={{
                expandedRowRender: (record) => {
                  const entries = buildProductEntries(record.products);

                  return (
                    <div className="transport-routes-table__expanded">
                      {entries.length > 0 ? (
                        <div className="transport-routes-table__expanded-block">
                          <div className="transport-routes-table__expanded-title">
                            {t('transport:table.products')}
                          </div>
                          <div className="transport-routes-table__expanded-products">
                            {entries.map((entry) => (
                              <div key={entry.key} className="transport-routes-table__expanded-product">
                                <span className="transport-routes-table__cell-text transport-routes-table__cell-text--strong">
                                  {entry.name}
                                </span>
                                {entry.dimensionsLabel ? (
                                  <span className="transport-routes-table__dimension-line">
                                    {entry.dimensionsLabel}
                                  </span>
                                ) : null}
                                {entry.quantityLabel ? (
                                  <span className="transport-routes-table__pill">{entry.quantityLabel}</span>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <Descriptions
                        bordered
                        size="small"
                        column={2}
                        className="transport-routes-table__expanded-descriptions"
                      >
                        <Descriptions.Item label={t('transport:fields.loadingAddresses')}>
                          {renderExpandedList(record.loadingAddresses)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:fields.deliveryAddresses')}>
                          {renderExpandedList(record.deliveryAddresses)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:table.orderDate')}>
                          {formatDate(record.orderDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:fields.driver')}>
                          {record.driverName || '-'}
                          {record.driverPhone ? ` (${record.driverPhone})` : ''}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:fields.totalWeight')}>
                          {record.totalWeight === null ? '-' : `${record.totalWeight} kg`}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:fields.totalVolume')}>
                          {record.totalVolume === null ? '-' : `${record.totalVolume} m³`}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('transport:fields.notes')} span={2}>
                          {record.notes || '-'}
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  );
                },
                rowExpandable: (record) =>
                  record.loadingAddresses.length > 0 ||
                  record.deliveryAddresses.length > 0 ||
                  record.products.length > 0 ||
                  Boolean(record.notes) ||
                  Boolean(record.driverName),
              }}
              locale={{
                emptyText: <Empty description={t('transport:emptyState')} />,
              }}
              pagination={{
                pageSize: 25,
                showSizeChanger: true,
              }}
            />
          </Card>
      </div>

      <Modal
        title={t('transport:confirmDelete')}
        open={Boolean(pendingDelete)}
        onOk={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
        okButtonProps={{ danger: true }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{t('transport:confirmDeleteContent')}</p>
      </Modal>
    </>
  );
};

export default TransportRoutes;
